import React from 'react';
import { X, Save, CreditCard, CheckCircle } from 'lucide-react';
import { SubscriptionButton } from './SubscriptionButton';
import { useAuth } from '../../hooks/useAuth';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  requiresAuth?: boolean;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({ isOpen, onClose, requiresAuth = false }) => {
  if (!isOpen) return null;
  const { signIn } = useAuth();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Upgrade to Premium</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Save className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {requiresAuth ? 'Sign In Required' : "Save Your Analyses"}
            </h3>
            <p className="text-gray-600">
              {requiresAuth 
                ? 'Please sign in with Google to save your mortgage analyses.'
                : "If you're serious about meeting your mortgage..."
              }
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-sm text-gray-700">Save unlimited analyses</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-sm text-gray-700">Display up to 6 analyses at once</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Premium Plan</p>
                <p className="text-sm text-gray-600">Unlimited saves and features</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">$2</p>
                <p className="text-sm text-gray-600">per month</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            {requiresAuth ? (
              <button
                onClick={async () => { try { await signIn(); } finally { onClose(); } }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Sign In with Google
              </button>
            ) : (
              <SubscriptionButton className="flex-1" />
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
